import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { getJTI } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { useScopedQuery } from '@frontend/scope';
import { sentry } from '@frontend/tracking';
import { AudioScrubber, SpinningLoader, Text } from '@frontend/design-system';

export interface CachedAudioScrubberProps {
  onPlay?: () => void;
  customWidth?: number;
  filePath?: string;
  mediaId: string;
  singlePlayer?: boolean;
  tabIndex?: number;
  trackingId?: string;
  queryFn?: () => Promise<Response>;
  isCompactMode?: boolean;
  autoplay?: boolean;
  allowFetchError?: boolean;
  queryClientErrorFallback?: ReactNode;
}

class AudioFetchError extends Error {
  constructor(readonly status: number) {
    super('Failed to fetch audio');
  }
}

export const CachedAudioScrubber = ({
  onPlay,
  customWidth,
  filePath,
  mediaId,
  singlePlayer,
  tabIndex = 0,
  trackingId = 'cached-audioScrubber',
  queryFn,
  isCompactMode,
  autoplay,
  allowFetchError = false,
  queryClientErrorFallback,
}: CachedAudioScrubberProps) => {
  const { t } = useTranslation('phone');
  const token = getJTI();
  const reportedErrorRef = useRef<unknown>(undefined);

  /**
   * Why fetch the resource instead of loading it directly into the audio component?
   *
   * Doing this allows us to cache the resource, so that the audio component doesn't re-fetch it every time it's rendered.
   */
  const { data, isLoading, isError, error } = useScopedQuery({
    queryKey: ['cached-audio', mediaId],
    queryFn: async () => {
      let response: Response;

      if (queryFn) {
        response = await queryFn();
      } else if (filePath) {
        const url = new URL(filePath);
        if (url.searchParams.has('token')) {
          url.searchParams.delete('token');
        }

        response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        return undefined;
      }

      if (!allowFetchError && !response.ok) {
        throw new AudioFetchError(response.status);
      }

      return response.blob().then((myBlob) => {
        const objectURL = URL.createObjectURL(myBlob);
        return objectURL;
      });
    },
    enabled: !!mediaId && (!!filePath || !!queryFn),
    staleTime: Infinity,
  });

  const errorStatus = error instanceof AudioFetchError ? error.status : getErrorStatus(error);

  useEffect(() => {
    if (!isError || (errorStatus !== undefined && errorStatus < 500) || reportedErrorRef.current === error) return;

    const audioError = error instanceof Error ? error : new Error('Failed to load audio');
    reportedErrorRef.current = error;
    sentry.error({
      error: audioError,
      team: 'frontend-ops',
      topic: 'phone',
      addContext: {
        name: 'audio',
        context: {
          mediaId,
          status: errorStatus,
        },
      },
    });
  }, [error, errorStatus, isError, mediaId]);

  if (isError) {
    // Only API errors thrown by queryFn use the fallback; failed audio URL responses remain visible errors.
    if (
      !(error instanceof AudioFetchError) &&
      errorStatus !== undefined &&
      errorStatus >= 400 &&
      errorStatus < 500 &&
      queryClientErrorFallback !== undefined
    ) {
      return <>{queryClientErrorFallback}</>;
    }

    return <Text color='error'>{t('Error loading audio')}</Text>;
  }

  if (isLoading) {
    return <SpinningLoader size='small' />;
  }

  return data ? (
    <AudioScrubber
      key={mediaId}
      trackingId={trackingId}
      autoSize
      autoplay={autoplay}
      onPlay={onPlay}
      singlePlayer={singlePlayer}
      src={data}
      isCompactMode={isCompactMode}
      css={customWidth ? { maxWidth: `${customWidth}px` } : { maxWidth: '100%' }}
      tabIndex={tabIndex}
    />
  ) : null;
};

const getErrorStatus = (error: unknown) => {
  if (typeof error !== 'object' || error === null) return undefined;
  if ('status' in error && typeof error.status === 'number') return error.status;

  if ('cause' in error && typeof error.cause === 'object' && error.cause !== null && 'status' in error.cause) {
    return typeof error.cause.status === 'number' ? error.cause.status : undefined;
  }

  return undefined;
};
